import React, { useState, useEffect, useRef } from "react";
import { InspectionsClient } from "../web-api-client.ts";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { Button, InputGroup, Form, Card } from "react-bootstrap";
import BackButton from "./BackButton.js";
import { isMobileDevice } from "./utils/utils.js";
import ClearableInput from "./ClearableInput.js";
import ScannerLauncher from "./ScannerLauncher.js";
import Loader from "./Loader.js";
import UserContext from "../providers/UserContext.js";

const Inspections = ({ status: initialFilterStatus }) => {
  const { barcode } = useParams(); // (currently unused, leaving as-is)
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState(initialFilterStatus || false); // (currently unused, leaving as-is)

  const [sortColumn, setSortColumn] = useState("inspectionDate");
  const [sortDirection, setSortDirection] = useState("desc");

  const [filtersReady, setFiltersReady] = useState(false);
  const [filters, setFilters] = useState({ status: null });

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const hasLoadedRef = useRef(false);
  const reqIdRef = useRef(0);

  const user = React.useContext(UserContext);

  const itemsPerPage = 15;

  // Utility to build query string
  const buildQuery = () => {
    const params = new URLSearchParams();
    if (searchTerm) params.set("search", searchTerm);
    if (filters.status !== null && filters.status !== undefined) {
      params.set("status", String(filters.status));
    }
    params.set("sort", sortColumn);
    params.set("dir", sortDirection);
    params.set("page", String(currentPage));
    return `?${params.toString()}`;
  };

  const handleRowClick = (inspectionId) => {
    navigate(`/dvi/${inspectionId}`);
  };

  const populateInspectionData = async (page, state) => {
    // Prevent stale requests from overwriting newer ones
    const reqId = ++reqIdRef.current;

    setLoading(true);
    const client = new InspectionsClient();

    const {
      searchTerm: s,
      sortColumn: sc,
      sortDirection: sd,
      filters: f,
    } =
      state ?? {
        searchTerm,
        sortColumn,
        sortDirection,
        filters,
      };

    try {
      const data = await client.getInspectionList(
        user.dealerId,
        page,
        itemsPerPage,
        s,
        sc,
        sd,
        f?.status ?? null
      );

      // Ignore stale results
      if (reqId !== reqIdRef.current) return;

      setInspections(data.items);
      setTotalPages(data.totalPages);
    } finally {
      // Only clear loading if this is still the latest request
      if (reqId === reqIdRef.current) setLoading(false);
    }
  };

  // Initial read from querystring on mount
  useEffect(() => {
    const fetchInitialData = async () => {
      const initialSearch = searchParams.get("search") || "";
      const statusStr = searchParams.get("status");
      const initialStatus =
        statusStr !== null && !Number.isNaN(Number(statusStr))
          ? Number(statusStr)
          : null;
      const initialSort = searchParams.get("sort") || "inspectionDate";
      const initialDir = searchParams.get("dir") || "desc";
      const initialPage = parseInt(searchParams.get("page"), 10) || 1;

      setSearchTerm(initialSearch);
      setFilters({ status: initialStatus });
      setSortColumn(initialSort);
      setSortDirection(initialDir);
      setCurrentPage(initialPage);

      setFiltersReady(true);
    };

    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Once we have initial filters, load data exactly once, then allow URL syncing & param watching
  useEffect(() => {
    if (!filtersReady) return;

    populateInspectionData(currentPage, {
      searchTerm,
      sortColumn,
      sortDirection,
      filters,
    });

    hasLoadedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersReady]);

  // Watch URL param changes (back/forward nav, manual edits)
  useEffect(() => {
    if (!hasLoadedRef.current) return;

    const newSearch = searchParams.get("search") || "";
    const newSort = searchParams.get("sort") || "inspectiondate";
    const newDir = searchParams.get("dir") || "desc";
    const statusRaw = searchParams.get("status");
    const newStatus = statusRaw !== null ? Number(statusRaw) : null;
    const newPage = parseInt(searchParams.get("page"), 10) || 1;

    setSearchTerm(newSearch);
    setFilters({ status: newStatus });
    setSortColumn(newSort);
    setSortDirection(newDir);
    setCurrentPage(newPage);
  }, [searchParams]);

  // Single “source of truth” fetch effect (no other direct fetching on searchTerm change)
  useEffect(() => {
    if (!hasLoadedRef.current) return;

    populateInspectionData(currentPage, {
      searchTerm,
      sortColumn,
      sortDirection,
      filters,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, filters, sortColumn, sortDirection, currentPage]);

  // Keep URL in sync with state (but avoid doing this before initial hydration)
  useEffect(() => {
    if (!hasLoadedRef.current) return;
    navigate(buildQuery(), { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, filters, sortColumn, sortDirection, currentPage]);

  const resetList = async () => {
    // Don’t fetch directly; just reset state and let the single effect fetch
    setSearchTerm("");
    setFilters({ status: null });
    setSortColumn("inspectiondate");
    setSortDirection("desc");
    setCurrentPage(1);
  };

  const handleAddInspection = () => {
    navigate("/dvi/new");
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleScan = (scannedBarcode) => {
    setSearchTerm(scannedBarcode);
    setCurrentPage(1);
  };

  const handleSearch = async (eOrString) => {
    // Enter/search triggers should not fetch directly; they should just ensure state is set
    const value =
      typeof eOrString === "string"
        ? eOrString
        : eOrString?.target?.value ?? searchTerm;

    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
    setCurrentPage(1);
  };

  const handleStatusChange = (e) => {
    const v = e.target.value;
    setFilters({ status: v === "" ? null : Number(v) });
    setCurrentPage(1);
  };

  const clearStatus = () => {
    setFilters({ status: null });
    setCurrentPage(1);
  };

  const clearAll = () => {
    setSearchTerm("");
    setFilters({ status: null });
    setSortColumn("inspectiondate");
    setSortDirection("desc");
    setCurrentPage(1);
  };

  // ===== Mobile Card Renderer =====
  const renderMobileCard = (i) => (
    <Card
      key={i.id}
      className="mb-4 mt-4 shadow-sm"
      role="button"
      onClick={() => handleRowClick(i.id)}
      style={{ transition: 'transform 0.2s' }}
    >
      <Card.Body className="p-3">
        {/* Vehicle Info - Main Header */}
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div>
            <h6 className="mb-1 fw-bold">
              {i.vehicleYear} {i.vehicleMake} {i.vehicleModel}
            </h6>
            <div className="text-muted"> {i.vehicleColor} </div>
          </div>
          <span className={`badge ${i.status ? 'bg-success' : 'bg-warning text-dark'} ms-2`}>
            {i.status ? "Completed" : "In Progress"}
          </span>
        </div>

        {/* Divider */}
        <hr className="my-2" style={{ opacity: 0.1 }} />

        {/* Details Section */}
        <div className="small">
          <div className="d-flex align-items-center mb-2 text-muted">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" className="me-2" viewBox="0 0 16 16">
              <path d="M11 6.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1z" />
              <path d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5zM1 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4H1z" />
            </svg>
            Inspection Date: {new Date(i.inspectionDate).toLocaleString(undefined, {
              dateStyle: "medium",
              timeStyle: "short",
              hour12: true,
            })}
          </div>

          <div className="d-flex align-items-center text-muted">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" className="me-2" viewBox="0 0 16 16">
              <path d="M1 2.5A1.5 1.5 0 0 1 2.5 1h3A1.5 1.5 0 0 1 7 2.5v3A1.5 1.5 0 0 1 5.5 7h-3A1.5 1.5 0 0 1 1 5.5v-3zM2.5 2a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3zm6.5.5A1.5 1.5 0 0 1 10.5 1h3A1.5 1.5 0 0 1 15 2.5v3A1.5 1.5 0 0 1 13.5 7h-3A1.5 1.5 0 0 1 9 5.5v-3zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3zM1 10.5A1.5 1.5 0 0 1 2.5 9h3A1.5 1.5 0 0 1 7 10.5v3A1.5 1.5 0 0 1 5.5 15h-3A1.5 1.5 0 0 1 1 13.5v-3zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3zm6.5.5A1.5 1.5 0 0 1 10.5 9h3a1.5 1.5 0 0 1 1.5 1.5v3a1.5 1.5 0 0 1-1.5 1.5h-3A1.5 1.5 0 0 1 9 13.5v-3zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3z" />
            </svg>
            Stock #: <span className=" ms-1">{i.vehicleStockNumber}</span>
          </div>
        </div>
      </Card.Body>
    </Card>

  );

  return (
    <div className="container mb-5" style={{ marginBottom: "90px!important" }}>
      <BackButton />
      <h1>Inspections</h1>

      <div className="row g-2 align-items-center">
        <div className="col-12 col-md-8 d-flex flex-wrap align-items-center gap-3">
          <InputGroup style={{ maxWidth: "300px" }}>
            <ClearableInput
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onEnter={handleSearch}
              onClear={resetList}
            />
            <ScannerLauncher
              onBarcodeDetected={handleScan}
              mode="button"
              buttonClasses="ms-2"
            />
          </InputGroup>

          {/* Status filter */}
          <Form.Select
            size="sm"
            style={{ width: 200 }}
            value={
              filters.status === null || filters.status === undefined
                ? ""
                : String(filters.status)
            }
            onChange={handleStatusChange}
            aria-label="Filter by status"
          >
            <option value="">All statuses</option>
            <option value="0">In-Progress</option>
            <option value="1">Completed</option>
          </Form.Select>

          {/* Clear status (only shows when a status is applied) */}
          {filters.status !== null && filters.status !== undefined && (
            <Button variant="outline-secondary" size="sm" onClick={clearStatus}>
              Clear status
            </Button>
          )}

          {/* Optional: clear everything */}
          <Button variant="outline-dark" size="sm" onClick={clearAll}>
            Clear all
          </Button>
        </div>


        <div className="col-12 col-md-4 d-flex justify-content-md-end">
          <button
            className="btn btn-primary w-100 w-md-auto"
            onClick={handleAddInspection}
          >
            Add Inspection
          </button>
        </div>
      </div>

      {loading ? (
        <Loader />
      ) : (
        <div>
          <div className="d-none d-md-block">
            <table className="table table-hover align-middle">
              <thead>
                <tr>
                  <th
                    onClick={() => handleSort("vehicleStockNumber")}
                    style={{ cursor: "pointer" }}
                  >
                    Stock Number
                    {sortColumn === "vehicleStockNumber" &&
                      (sortDirection === "asc" ? "▲" : "▼")}
                  </th>
                  <th
                    onClick={() => handleSort("vehicleYear")}
                    style={{ cursor: "pointer" }}
                  >
                    Year{" "}
                    {sortColumn === "vehicleYear" &&
                      (sortDirection === "asc" ? "▲" : "▼")}
                  </th>
                  <th
                    onClick={() => handleSort("vehicleMake")}
                    style={{ cursor: "pointer" }}
                  >
                    Make{" "}
                    {sortColumn === "vehicleMake" &&
                      (sortDirection === "asc" ? "▲" : "▼")}
                  </th>
                  <th
                    onClick={() => handleSort("vehicleModel")}
                    style={{ cursor: "pointer" }}
                  >
                    Model{" "}
                    {sortColumn === "vehicleModel" &&
                      (sortDirection === "asc" ? "▲" : "▼")}
                  </th>
                  <th
                    onClick={() => handleSort("vehicleModel")}
                    style={{ cursor: "pointer" }}
                  >
                    Color{" "}
                    {sortColumn === "vehicleColor" &&
                      (sortDirection === "asc" ? "▲" : "▼")}
                  </th>
                  <th
                    onClick={() => handleSort("status")}
                    style={{ cursor: "pointer" }}
                  >
                    Status{" "}
                    {sortColumn === "status" &&
                      (sortDirection === "asc" ? "▲" : "▼")}
                  </th>

                  {!isMobileDevice() && (
                    <th
                      onClick={() => handleSort("inspectionDate")}
                      style={{ cursor: "pointer" }}
                    >
                      Inspection Date{" "}
                      {sortColumn === "inspectionDate" &&
                        (sortDirection === "asc" ? "▲" : "▼")}
                    </th>
                  )}

                  <th
                    onClick={() => handleSort("viewCount")}
                    style={{ cursor: "pointer" }}
                  >
                    View Count{" "}
                    {sortColumn === "viewCount" &&
                      (sortDirection === "asc" ? "▲" : "▼")}
                  </th>
                  <th
                    onClick={() => handleSort("scanCount")}
                    style={{ cursor: "pointer" }}
                  >
                    Scan Count{" "}
                    {sortColumn === "scanCount" &&
                      (sortDirection === "asc" ? "▲" : "▼")}
                  </th>
                </tr>
              </thead>

              <tbody>
                {inspections.map((inspection) => (
                  <tr
                    key={inspection.id}
                    onClick={() => handleRowClick(inspection.id)}
                    style={{ cursor: "pointer" }}
                  >
                    <td>{inspection.vehicleStockNumber}</td>
                    <td>{inspection.vehicleYear}</td>
                    <td>{inspection.vehicleMake}</td>
                    <td>{inspection.vehicleModel}</td>
                    <td>{inspection.vehicleColor}</td>
                    <td>{inspection.status ? "Completed" : "In-Progress"}</td>

                    {!isMobileDevice() && (
                      <td>
                        {new Date(inspection.inspectionDate).toLocaleString(
                          undefined,
                          {
                            dateStyle: "medium",
                            timeStyle: "short",
                            hour12: true,
                          }
                        )}
                      </td>
                    )}

                    <td>{inspection.viewCount || 0}</td>
                    <td>{inspection.scanCount || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="d-md-none">
            {inspections.map(renderMobileCard)}
          </div>
        </div>



      )}

      {/* Pagination */}
      <nav className="mb-6">
        <ul className="pagination justify-content-end">
          <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
            <button
              className="page-link"
              onClick={() => handlePageChange(currentPage - 1)}
            >
              Previous
            </button>
          </li>

          {Array.from({ length: totalPages }, (_, index) => (
            <li
              key={index}
              className={`page-item ${currentPage === index + 1 ? "active" : ""}`}
            >
              <button
                className="page-link"
                onClick={() => handlePageChange(index + 1)}
              >
                {index + 1}
              </button>
            </li>
          ))}

          <li
            className={`page-item ${currentPage === totalPages ? "disabled" : ""
              }`}
          >
            <button
              className="page-link"
              onClick={() => handlePageChange(currentPage + 1)}
            >
              Next
            </button>
          </li>
        </ul>
      </nav>
    </div>
  );
};

export default Inspections;
